import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Action } from 'redux'

import { useAction } from '@hozana/hooks/useAction'
import { useTimeout } from '@hozana/hooks/useTimeout'
import { useDispatch, useSelector } from '@hozana/redux/hooks'

import { addPrompt, openPopin, unmountPrompt, updatePromptState } from 'general/actions'
import { QUERY_ACTION } from 'general/constants'
import { POPINS } from 'general/managers/popins/constants'
import { selectPopin, selectPrompts } from 'general/selectors'

import { PromptType } from './constants'
import type { TPromptState } from './types'

const isMaxPromptsReached = (prompts: TPromptState[], maxPromptsPerSession: number) =>
  prompts.filter(({ state }) => state === 'closed' || state === 'open').length >= maxPromptsPerSession

const getLastPromptOfTypeIndex = (type: PromptType, prompts: TPromptState[]): number =>
  prompts.map((prompt) => prompt.type).lastIndexOf(type)

/**
 * A prompt has the priority if there is no other pending prompt with a higher priority.
 * A prompt has a higher priority if its type has a lower value.
 * @param prompt Does this prompt as the priority...
 * @param prompts ...among those?
 */
const hasPriority = (prompt: TPromptState, prompts: TPromptState[]) =>
  prompt && !prompts.find(({ type, state }) => state === 'pending' && type < prompt.type)

export type TPromptsManagementProps = {
  type: PromptType
  /** Whether or not the prompt should show up once the delay is over */
  show?: boolean
  /** In milliseconds. The timer will start once show is true */
  delay?: number
  /** An action value that will force the prompt to show up if used in a url query-action (`&action=yourActionKey`) */
  actionKey?: QUERY_ACTION
  /** Max prompt number that this one should fit in. Default: 2 (so by default, won't be prompted if 2 have already been prompted). */
  maxPromptsPerSession?: number
  /** Delay min in ms after last prompt has been closed. Default: 60000 (60s). */
  delayMinAfterLastPrompt?: number
  /** Delay min in ms after last popin has been closed. Default: 20000 (20s). */
  delayMinAfterLastPopin?: number
  onPop?: () => void
} & (
  | {
      /** Popin name that can be associated with the 'popinOptions' prop to pass its options */
      popinName: TObjectValues<typeof POPINS>
      popinOptions?: Record<string, any>
      onClosePopin?: () => void
    }
  | ((
      | {
          /** Function that will be called to prompt. /!\ Using that prop, you are responsible for passing a 'closed' prop to true once the prompt is closed. */
          pop: () => void
        }
      | {
          /** Action that will be dispatched to prompt. /!\ Using that prop, you are responsible for passing a 'closed' prop to true once the prompt is closed. */
          action: Action
        }
    ) & {
      /** When using a custom 'pop' or 'action' prop, set this prop to true once the prompt is closed to inform the prompt management. */
      closed: boolean
    })
)

/**
 * ### usePromptsManagement ###
 *
 * Manage prompts (automatic popins/banners) to avoid conflicts and overdose
 */
export const usePromptsManagement = ({
  type,
  show = false,
  delay,
  actionKey,
  onPop,
  maxPromptsPerSession = 2,
  delayMinAfterLastPrompt = 60000, // 1min
  delayMinAfterLastPopin = 20000, // 20s
  ...otherProps
}: TPromptsManagementProps): void => {
  const { popinName = null, popinOptions = null, onClosePopin = null } = 'popinName' in otherProps ? otherProps : {}
  const { action = null } = 'action' in otherProps ? otherProps : {}
  const { pop: popper = null } = 'pop' in otherProps ? otherProps : {}
  const { closed = false } = 'closed' in otherProps ? otherProps : {}

  const dispatch = useDispatch()

  const prompts = useSelector(selectPrompts)
  const { isOpen: isAnyPopinOpen, name: currentPopinName } = useSelector(selectPopin)
  const isAnyPromptOpen = useMemo(() => !!prompts.find((promptsPrompt) => promptsPrompt.state === 'open'), [prompts])

  /* ADD/CANCEL/DELETE PROMPT IN THE STORE */

  const [added, setAdded] = useState<boolean>(false) // Null as long as the prompt has not been added to the store
  const promptIndex = added ? getLastPromptOfTypeIndex(type, prompts) : -1
  const prompt = useMemo(() => (promptIndex >= 0 ? prompts[promptIndex] : null), [promptIndex, prompts]) // Null as long as promptIndex is not found
  const maxPromptsReached = isMaxPromptsReached(prompts, maxPromptsPerSession)

  // Add prompt into the store
  useEffect(() => {
    if (!added && show && !maxPromptsReached) {
      // If the prompt has not been added to the store yet, but should be now, and if the max prompts per session has not been reached yet...
      // ...record the previous length of the prompts array as a negative value
      // ...add the prompt to the store
      dispatch(addPrompt(type))
      setAdded(true)
    }
  }, [added, dispatch, maxPromptsReached, show, type])

  // Cancel prompt in the store
  useEffect(() => {
    if (!show && added && prompt?.state === 'pending') {
      dispatch(updatePromptState(promptIndex, 'canceled'))
      setAdded(false)
    }
  }, [added, dispatch, prompt?.state, promptIndex, show])

  // Unmount prompt in the store on unmount
  useEffect(
    () => () => {
      if (promptIndex >= 0) {
        dispatch(unmountPrompt(promptIndex))
      }
    },
    [dispatch, promptIndex],
  )

  /* PROMPT DELAY */

  const [isPromptReady, setIsPromptReady] = useState(!delay)

  // Reinitialize isPromptReady if delay props changes
  useEffect(() => setIsPromptReady(!delay), [delay])

  // Sets isPromptReady to true when delay is reached
  useTimeout(() => setIsPromptReady(true), delay, !show)

  /* PROMPTS INTERVAL */

  // promptsInterval === true as soon as a prompt is open and until {delayMinAfterLastPrompt} ms as passed since it has been closed
  const [promptsInterval, setPromptsInterval] = useState(false)

  // Sets popinsInterval to true as soon as a popin is open
  useEffect(() => {
    if (isAnyPromptOpen) {
      setPromptsInterval(true)
    }
  }, [isAnyPromptOpen])

  // Sets promptsInterval to false when delayMinAfterLastPrompt is reached after the prompts has been closed (promptsInterval && !isAnyPromptOpen)
  useTimeout(() => setPromptsInterval(false), delayMinAfterLastPrompt, !promptsInterval || isAnyPromptOpen)

  /* POPINS INTERVAL */

  // popinsInterval === true as soon as a popin is open and until {delayMinAfterLastPopin} ms as passed since it has been closed
  const [popinsInterval, setPopinsInterval] = useState(false)

  // Sets popinsInterval to true as soon as a popin is open
  useEffect(() => {
    if (isAnyPopinOpen) {
      setPopinsInterval(true)
    }
  }, [isAnyPopinOpen])

  // Sets popinsInterval to false when delayMinAfterLastPopin is reached after the popin has been closed (popinsInterval && !isAnyPopinOpen)
  useTimeout(() => setPopinsInterval(false), delayMinAfterLastPopin, !popinsInterval || isAnyPopinOpen)

  /* PROMPT OPENING/CLOSING */

  /** Open the prompt */
  const pop = useCallback(() => {
    if (popinName) {
      dispatch(openPopin(popinName, popinOptions))
    } else if (action) {
      dispatch(action)
    } else if (popper) {
      popper()
    }
  }, [popinName, action, popper, dispatch, popinOptions])

  // Forced opening by url query
  useAction(pop, [pop], actionKey, { disabled: !actionKey })

  // Opening
  useEffect(() => {
    if (
      //                                                        If...
      show && //                                                ...the show prop is true
      prompt?.state === 'pending' && //                         ...the prompt is pending
      isPromptReady && //                                       ...its delay has passed
      !isAnyPopinOpen && //                                     ...no popin is open
      !popinsInterval && //                                     ...no popin was open during the last {delayMinAfterLastPopin}
      !isAnyPromptOpen && //                                    ...no prompt is open
      !promptsInterval && //                                    ...no prompt was open during the last {delayMinAfterLastPrompt}
      !maxPromptsReached && //                                  ...the maximum prompts has not been reached yet
      hasPriority(prompt, prompts) //                           ...the prompt has the priority among the others
    ) {
      // !!! POOOOOP !!! //

      // Update the redux state to prevent any other prompt to open
      dispatch(updatePromptState(promptIndex, 'open'))

      pop()

      // Fire the onPop callback if any
      if (onPop) {
        onPop()
      }
    }
  })

  // Closing
  useEffect(() => {
    const promptPopinJustClosed = //                         Prompt popin just closed if...
      popinName && //                                        ...prompt is a popin AND
      prompt?.state === 'open' && //                         ... is still considered as open AND
      (!isAnyPopinOpen || currentPopinName !== popinName) // ... (there is no open popin OR the current popin is not this one).
    // Other prompt just closed if prompt is an other prompt AND is still considered as open AND is closed.
    const otherPromptJustClosed = (action || popper) && prompt?.state === 'open' && closed

    if (promptPopinJustClosed || otherPromptJustClosed) {
      dispatch(updatePromptState(promptIndex, 'closed'))
      if (onClosePopin) {
        onClosePopin()
      }
    }
  }, [
    action,
    closed,
    currentPopinName,
    dispatch,
    isAnyPopinOpen,
    onClosePopin,
    popinName,
    popper,
    prompt?.state,
    promptIndex,
  ])
}
